/**
 * Collection Manager
 * Handles grid switching, filter drawer, dynamic loading, and stable counts.
 */
class CollectionManager {
  constructor() {
    this.selectors = {
      grid: '[data-collection-grid]',
      gridSwitcher: '[data-grid-switcher]',
      gridToggle: '[data-grid-toggle]',
      gridSwitcherMobile: '[data-grid-switcher-mobile]',
      gridToggleMobile: '[data-grid-toggle-mobile]',
      filterOpen: '[data-filter-open]',
      filterClose: '[data-filter-close]',
      filterOverlay: '[data-filter-overlay]',
      filterDrawer: '[data-filter-drawer]',
      filterToggle: '[data-filter-toggle]',
      filterContent: '[data-filter-content]',
      filterForm: '#CollectionFiltersForm',
      filterCount: '[data-filter-count]',
      filterTags: '.filter-drawer__active-facets-wrapper',
      pagination: '[data-collection-pagination]',
      loading: '[data-infinite-scroll-loading]',
      loadMoreWrapper: '[data-load-more-wrapper]',
      loadMoreButton: '[data-load-more-button]',
      productCount: '[data-product-count]'
    };

    this.grid = document.querySelector(this.selectors.grid);
    this.switcher = document.querySelector(this.selectors.gridSwitcher);
    this.gridToggle = document.querySelector(this.selectors.gridToggle);
    this.gridToggleMobile = document.querySelector(this.selectors.gridToggleMobile);
    this.filterBtn = document.querySelector(this.selectors.filterOpen);
    this.drawer = document.querySelector(this.selectors.filterDrawer);
    this.overlay = document.querySelector(this.selectors.filterOverlay);
    this.closeBtn = document.querySelector(this.selectors.filterClose);
    this.filterForm = this.drawer?.querySelector(this.selectors.filterForm) || null;

    this.isLoading = false;
    this.isAutoFilling = false;
    this.isCountLoading = false;
    this.isCountLocked = false;
    this.hasScrolled = false;
    this.resolvedTotalProducts = null;
    this.pageCache = new Map();
    this.bufferedNodes = [];
    this.countRequestId = 0;
    this.prefetchHandle = null;
    this.fillCheckFrame = null;

    if (this.grid) {
      this.init();
    }
  }

  init() {
    this.refreshElements();
    this.bindEvents();
    this.initGrid();
    this.initGridMobile();
    this.bindDynamicLoadingEvents();
    void this.bootDynamicLoading();
  }

  refreshElements() {
    this.grid = document.querySelector(this.selectors.grid);
    this.pagination = document.querySelector(this.selectors.pagination);
    this.loadingIndicator = document.querySelector(this.selectors.loading);
    this.loadMoreWrapper = document.querySelector(this.selectors.loadMoreWrapper);
    this.loadMoreButton = document.querySelector(this.selectors.loadMoreButton);
    this.countDisplays = Array.from(document.querySelectorAll(this.selectors.productCount));
    this.loadingMode = this.getLoadingMode();
  }

  bindEvents() {
    if (this.gridToggle) {
      this.gridToggle.addEventListener('click', () => {
        const currentCols = localStorage.getItem('haight-grid-cols') || '4';
        const newCols = currentCols === '4' ? '3' : '4';
        this.updateGrid(newCols);
      });
    }

    if (this.gridToggleMobile) {
      this.gridToggleMobile.addEventListener('click', () => {
        const currentCols = localStorage.getItem('haight-grid-cols-mobile') || '2';
        const newCols = currentCols === '2' ? '1' : '2';
        this.updateGridMobile(newCols);
      });
    }

    if (this.filterBtn) {
      this.filterBtn.addEventListener('click', () => this.toggleDrawer(true));
    }
    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', () => this.toggleDrawer(false));
    }
    if (this.overlay) {
      this.overlay.addEventListener('click', () => this.toggleDrawer(false));
    }

    if (this.loadMoreButton) {
      this.loadMoreButton.addEventListener('click', async () => {
        await this.loadNextPage();
      });
    }

    if (this.drawer) {
      this.drawer.addEventListener('click', (event) => {
        const toggle = event.target.closest(this.selectors.filterToggle);
        if (toggle) {
          const group = toggle.closest('.filter-group');
          const shouldOpen = !group?.classList.contains('is-active');

          this.drawer.querySelectorAll('.filter-group.is-active').forEach((activeGroup) => {
            if (activeGroup !== group) {
              activeGroup.classList.remove('is-active');
            }
          });

          if (group) {
            group.classList.toggle('is-active', shouldOpen);
          }
          return;
        }

        const removeBtn = event.target.closest('[data-filter-remove]');
        const clearAllBtn = event.target.closest('[data-filter-clear-all]');
        if (removeBtn || clearAllBtn) {
          event.preventDefault();
          const url = (removeBtn || clearAllBtn).getAttribute('href');
          void this.onFilterChange(url);
          return;
        }

        const priceSubmit = event.target.closest('[data-filter-price-submit]');
        if (priceSubmit) {
          event.preventDefault();
          void this.onFilterChange();
        }
      });

      this.drawer.addEventListener('change', (event) => {
        if (event.target.classList.contains('filter-price-input')) {
          return;
        }

        if (event.target.closest(this.selectors.filterForm)) {
          void this.onFilterChange();
        }
      });
    }

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.drawer?.classList.contains('is-open')) {
        this.toggleDrawer(false);
      }
    });
  }

  bindDynamicLoadingEvents() {
    const onScroll = () => {
      this.hasScrolled = true;
      if (this.loadingMode === 'infinite') {
        this.handleScroll();
      }
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('touchmove', onScroll, { passive: true });

    window.addEventListener(
      'resize',
      () => {
        if (this.loadingMode === 'infinite') {
          this.scheduleFillCheck();
        }
      },
      { passive: true }
    );
  }

  async bootDynamicLoading() {
    this.refreshElements();
    if (!this.grid) return;

    const countPromise = this.resolveTotalProducts();

    if (this.shouldUseDynamicLoading()) {
      await this.prefillPreviousPages();
      await this.ensureInitialVisibleProducts();

      if (this.loadingMode === 'infinite') {
        this.scheduleFillCheck();
      }

      this.prefetchNextPage();
    } else {
      this.updateLoadMoreVisibility();
    }

    await countPromise;
    this.updateLoadMoreVisibility();
  }

  initGrid() {
    if (!this.gridToggle) return;

    const savedGrid = localStorage.getItem('haight-grid-cols') || '4';
    this.updateGrid(savedGrid);
  }

  initGridMobile() {
    if (!this.gridToggleMobile) return;

    const savedGridMobile = localStorage.getItem('haight-grid-cols-mobile') || '2';
    this.updateGridMobile(savedGridMobile);
  }

  updateGrid(cols) {
    if (!this.grid) return;

    this.grid.classList.remove('md:grid-cols-3', 'md:grid-cols-4');
    this.grid.classList.add(`md:grid-cols-${cols}`);

    if (this.gridToggle) {
      const icon3 = this.gridToggle.querySelector('.grid-icon-3');
      const icon4 = this.gridToggle.querySelector('.grid-icon-4');

      if (cols === '3') {
        icon3?.classList.remove('hidden');
        icon4?.classList.add('hidden');
        this.gridToggle.setAttribute('aria-label', 'Mudar para 4 colunas');
      } else {
        icon3?.classList.add('hidden');
        icon4?.classList.remove('hidden');
        this.gridToggle.setAttribute('aria-label', 'Mudar para 3 colunas');
      }
    }

    localStorage.setItem('haight-grid-cols', cols);
  }

  updateGridMobile(cols) {
    if (!this.grid) return;

    this.grid.classList.remove('grid-cols-1', 'grid-cols-2');
    this.grid.classList.add(`grid-cols-${cols}`);

    if (this.gridToggleMobile) {
      const icon1 = this.gridToggleMobile.querySelector('.grid-icon-mobile-1');
      const icon2 = this.gridToggleMobile.querySelector('.grid-icon-mobile-2');

      if (cols === '1') {
        icon1?.classList.remove('hidden');
        icon2?.classList.add('hidden');
        this.gridToggleMobile.setAttribute('aria-label', 'Mudar para 2 colunas');
      } else {
        icon1?.classList.add('hidden');
        icon2?.classList.remove('hidden');
        this.gridToggleMobile.setAttribute('aria-label', 'Mudar para 1 coluna');
      }
    }

    localStorage.setItem('haight-grid-cols-mobile', cols);
  }

  getLoadingMode() {
    if (!this.grid) return 'pagination';
    return this.grid.dataset.loadingMode || (this.grid.dataset.infiniteScroll === 'true' ? 'infinite' : 'pagination');
  }

  shouldUseDynamicLoading() {
    return this.loadingMode === 'infinite' || this.loadingMode === 'load-more';
  }

  shouldResolveFilteredCount() {
    return this.grid?.dataset.countMode === 'filtered';
  }

  getConfiguredPageSize() {
    if (!this.grid) return 40;

    const size = parseInt(this.grid.dataset.pageSize || this.grid.dataset.prefillTarget || '40', 10);
    return Number.isFinite(size) && size > 0 ? size : 40;
  }

  getCurrentPage() {
    if (!this.pagination) return 1;

    const currentPage = parseInt(this.pagination.dataset.currentPage || '1', 10);
    return Number.isFinite(currentPage) && currentPage > 0 ? currentPage : 1;
  }

  getTotalPages() {
    if (!this.pagination) return 1;

    const totalPages = parseInt(this.pagination.dataset.totalPages || '1', 10);
    return Number.isFinite(totalPages) && totalPages > 0 ? totalPages : 1;
  }

  getCurrentPageVisibleCount() {
    const pageVisibleProducts = parseInt(this.pagination?.dataset.pageVisibleProducts || '0', 10);
    if (Number.isFinite(pageVisibleProducts) && pageVisibleProducts >= 0) {
      return pageVisibleProducts;
    }

    return this.getCurrentProductCount();
  }

  getCurrentProductCount() {
    if (!this.grid) return 0;
    return this.grid.querySelectorAll('.productCard').length;
  }

  getColumnsCount() {
    if (!this.grid) {
      return window.innerWidth < 750 ? 2 : 4;
    }

    if (window.innerWidth < 750) {
      return this.grid.classList.contains('grid-cols-1') ? 1 : 2;
    }

    if (this.grid.classList.contains('md:grid-cols-3')) {
      return 3;
    }

    return 4;
  }

  shouldAutoloadMore() {
    if (this.loadingMode !== 'infinite') return false;
    if (!this.bufferedNodes.length && !this.getNextPageLink()) return false;

    const currentCount = this.getCurrentProductCount();
    const columns = this.getColumnsCount();
    const hasIncompleteLastRow = currentCount > 0 && currentCount % columns !== 0;
    const viewportNeedsMore = document.documentElement.scrollHeight <= window.innerHeight + 200;

    return hasIncompleteLastRow || viewportNeedsMore;
  }

  handleScroll() {
    if (this.loadingMode !== 'infinite') return;
    if (this.isLoading || this.isAutoFilling || !this.hasScrolled) return;

    if (this.scrollTimeout) return;
    this.scrollTimeout = setTimeout(() => {
      this.scrollTimeout = null;

      if (this.isLoading || this.isAutoFilling) return;

      const footer = document.querySelector('footer, .site-footer, #shopify-section-footer');
      const footerHeight = footer ? footer.offsetHeight : 0;
      const baseThreshold = window.innerWidth < 750 ? 500 : 800;
      const scrollThreshold = footerHeight + baseThreshold;

      const scrollPos = window.innerHeight + window.scrollY;
      const bodyHeight = Math.max(
        document.body.scrollHeight,
        document.body.offsetHeight,
        document.documentElement.clientHeight,
        document.documentElement.scrollHeight,
        document.documentElement.offsetHeight
      );

      if (scrollPos > bodyHeight - scrollThreshold) {
        void this.loadNextPage();
      }
    }, 150);
  }

  scheduleFillCheck() {
    if (this.loadingMode !== 'infinite') return;

    if (this.fillCheckFrame) {
      cancelAnimationFrame(this.fillCheckFrame);
    }

    this.fillCheckFrame = requestAnimationFrame(() => {
      this.fillCheckFrame = null;
      void this.ensureGridIsFilled();
    });
  }

  async ensureGridIsFilled() {
    if (this.loadingMode !== 'infinite') return;
    if (this.isLoading || this.isAutoFilling) return;
    if (!this.shouldAutoloadMore()) return;

    this.isAutoFilling = true;

    try {
      let attempts = 0;

      while (this.shouldAutoloadMore() && attempts < 6) {
        const previousCount = this.getCurrentProductCount();
        const didAppend = await this.loadNextPage({
          skipFillCheck: true,
          updateHistory: false
        });

        if (!didAppend || this.getCurrentProductCount() === previousCount) {
          break;
        }

        attempts += 1;
      }
    } finally {
      this.isAutoFilling = false;
    }
  }

  async ensureInitialVisibleProducts() {
    if (!this.shouldUseDynamicLoading()) return;
    if (this.isLoading || this.isAutoFilling) return;

    const target = this.getConfiguredPageSize();
    if (!target) return;

    this.isAutoFilling = true;

    try {
      let attempts = 0;

      while (this.getCurrentProductCount() < target && attempts < 10) {
        const remaining = target - this.getCurrentProductCount();
        const didAppend = await this.loadNextPage({
          skipFillCheck: true,
          updateHistory: false,
          maxAppendProducts: remaining
        });

        if (!didAppend) {
          break;
        }

        attempts += 1;
      }
    } finally {
      this.isAutoFilling = false;
    }
  }

  async prefillPreviousPages() {
    if (!this.shouldUseDynamicLoading() || !this.grid) return;

    const currentPage = this.getCurrentPage();
    if (currentPage <= 1) return;

    const anchorNode = this.grid.firstChild;

    for (let page = 1; page < currentPage; page += 1) {
      const pageUrl = this.buildPageUrl(page);

      try {
        const doc = await this.fetchPageDocument(pageUrl);
        const pageGrid = doc.querySelector(this.selectors.grid);
        if (!pageGrid) continue;

        const fragment = document.createDocumentFragment();
        Array.from(pageGrid.children).forEach((child) => {
          fragment.appendChild(child);
        });

        this.grid.insertBefore(fragment, anchorNode);
      } catch (error) {
        console.error(`Error prefilling page ${page}:`, error);
      }
    }
  }

  getNextPageLink(doc = document) {
    const pagination = doc.querySelector(this.selectors.pagination);
    return pagination?.querySelector('a.next, .next a') || null;
  }

  getProductCountForNode(node) {
    if (!(node instanceof Element)) return 0;
    if (node.matches('.collection__empty, .collectionDynamic__empty')) return 0;
    if (node.matches('.productCard')) return 1;
    return node.querySelectorAll('.productCard').length;
  }

  appendNodesWithLimit(nodes, maxProducts = Infinity) {
    if (!this.grid || !nodes.length) {
      return { appendedProducts: 0, appendedAny: false, remainingNodes: [] };
    }

    const fragment = document.createDocumentFragment();
    const remainingNodes = [];
    const productLimit = Number.isFinite(maxProducts) && maxProducts > 0 ? maxProducts : Infinity;
    let appendedProducts = 0;
    let appendedAny = false;
    let limitReached = false;

    nodes.forEach((node) => {
      if (!(node instanceof Element)) {
        return;
      }

      if (node.matches('.collection__empty, .collectionDynamic__empty')) {
        return;
      }

      if (limitReached) {
        remainingNodes.push(node);
        return;
      }

      const nodeProductCount = this.getProductCountForNode(node);
      fragment.appendChild(node);
      appendedAny = true;
      appendedProducts += nodeProductCount;

      if (appendedProducts >= productLimit) {
        limitReached = true;
      }
    });

    if (appendedAny) {
      this.grid.appendChild(fragment);
    }

    return { appendedProducts, appendedAny, remainingNodes };
  }

  flushBufferedNodes(maxProducts = Infinity) {
    if (!this.bufferedNodes.length) {
      return { appendedProducts: 0, appendedAny: false };
    }

    const nodes = this.bufferedNodes;
    this.bufferedNodes = [];

    const result = this.appendNodesWithLimit(nodes, maxProducts);
    this.bufferedNodes = result.remainingNodes;

    return result;
  }

  updateLoadMoreVisibility() {
    if (!this.loadMoreWrapper) return;

    if (this.loadingMode !== 'load-more') {
      this.loadMoreWrapper.classList.add('hidden');
      return;
    }

    if (this.bufferedNodes.length || this.getNextPageLink()) {
      this.loadMoreWrapper.classList.remove('hidden');
    } else {
      this.loadMoreWrapper.classList.add('hidden');
    }
  }

  setLoadingState(state) {
    this.isLoading = state;
    if (this.loadingIndicator) {
      this.loadingIndicator.style.display = state ? 'flex' : 'none';
      this.loadingIndicator.classList.remove('hidden');
    }

    if (this.loadMoreButton) {
      this.loadMoreButton.disabled = state;
      this.loadMoreButton.setAttribute('aria-busy', state ? 'true' : 'false');
    }
  }

  setResolvedTotalProducts(total) {
    this.resolvedTotalProducts = total;

    if (this.grid && Number.isFinite(total)) {
      this.grid.dataset.resolvedTotalProducts = String(total);
    }
  }

  getResolvedTotalProducts() {
    if (Number.isFinite(this.resolvedTotalProducts)) {
      return this.resolvedTotalProducts;
    }

    const datasetTotal = parseInt(this.grid?.dataset.resolvedTotalProducts || '', 10);
    if (Number.isFinite(datasetTotal)) {
      return datasetTotal;
    }

    const rawTotal = parseInt(this.grid?.dataset.totalProducts || '0', 10);
    return Number.isFinite(rawTotal) ? rawTotal : 0;
  }

  setCountLoading(state) {
    this.isCountLoading = state;
    this.updateCount();
  }

  async resolveTotalProducts() {
    if (!this.grid) return;

    const requestId = ++this.countRequestId;

    if (!this.shouldResolveFilteredCount()) {
      this.setResolvedTotalProducts(parseInt(this.grid.dataset.totalProducts || '0', 10) || 0);
      this.setCountLoading(false);
      return;
    }

    const totalPages = this.getTotalPages();
    const currentPage = this.getCurrentPage();
    const currentPageVisibleCount = this.getCurrentPageVisibleCount();

    this.setCountLoading(true);

    try {
      let resolvedTotal = currentPageVisibleCount;

      if (totalPages > 1) {
        const pageUrls = [];

        for (let page = 1; page <= totalPages; page += 1) {
          if (page !== currentPage) {
            pageUrls.push(this.buildPageUrl(page));
          }
        }

        const counts = await this.fetchVisibleCounts(pageUrls);
        resolvedTotal += counts.reduce((sum, count) => sum + count, 0);
      }

      if (requestId !== this.countRequestId) {
        return;
      }

      this.setResolvedTotalProducts(resolvedTotal);
    } catch (error) {
      console.error('Error resolving filtered total products:', error);

      if (requestId !== this.countRequestId) {
        return;
      }

      this.setResolvedTotalProducts(currentPageVisibleCount);
    } finally {
      if (requestId === this.countRequestId) {
        this.setCountLoading(false);
      }
    }
  }

  async fetchVisibleCounts(pageUrls) {
    if (!pageUrls.length) return [];

    const results = new Array(pageUrls.length).fill(0);
    let cursor = 0;

    const worker = async () => {
      while (cursor < pageUrls.length) {
        const index = cursor;
        cursor += 1;

        const doc = await this.fetchPageDocument(pageUrls[index]);
        const pagination = doc.querySelector(this.selectors.pagination);
        const pageGrid = doc.querySelector(this.selectors.grid);
        const visibleCount = parseInt(pagination?.dataset.pageVisibleProducts || '0', 10);

        results[index] = Number.isFinite(visibleCount)
          ? visibleCount
          : pageGrid?.querySelectorAll('.productCard').length || 0;
      }
    };

    const workers = Array.from({ length: Math.min(4, pageUrls.length) }, () => worker());
    await Promise.all(workers);

    return results;
  }

  buildPageUrl(page, base = window.location.href) {
    const url = new URL(base, window.location.origin);

    if (page <= 1) {
      url.searchParams.delete('page');
    } else {
      url.searchParams.set('page', String(page));
    }

    return `${url.pathname}${url.search}${url.hash}`;
  }

  fetchPageHtml(url) {
    if (this.pageCache.has(url)) {
      return this.pageCache.get(url);
    }

    const request = fetch(url, {
      headers: {
        'X-Requested-With': 'XMLHttpRequest'
      }
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        return response.text();
      })
      .catch((error) => {
        this.pageCache.delete(url);
        throw error;
      });

    this.pageCache.set(url, request);
    return request;
  }

  async fetchPageDocument(url) {
    const html = await this.fetchPageHtml(url);
    return new DOMParser().parseFromString(html, 'text/html');
  }

  prefetchNextPage() {
    if (!this.shouldUseDynamicLoading()) return;

    const nextLink = this.getNextPageLink();
    if (!nextLink) return;

    const pageUrl = nextLink.href;
    if (this.pageCache.has(pageUrl)) return;

    const schedule = () => {
      void this.fetchPageHtml(pageUrl).catch(() => { });
    };

    if (this.prefetchHandle) {
      clearTimeout(this.prefetchHandle);
    }

    // Usando setTimeout diretamente. O requestIdleCallback no iOS Safari
    // aguarda o fim absoluto do toque/scroll para ser executado, o que 
    // atrasava drasticamente o prefetch da próxima página.
    this.prefetchHandle = window.setTimeout(schedule, 300);
  }

  clearPageCache() {
    this.pageCache.clear();
  }

  syncPagination(newPagination) {
    if (!this.pagination) {
      this.pagination = document.querySelector(this.selectors.pagination);
    }
    if (!this.pagination) return;

    this.pagination.innerHTML = newPagination?.innerHTML || '';

    ['currentPage', 'totalPages', 'pageVisibleProducts'].forEach((key) => {
      if (newPagination?.dataset[key] != null) {
        this.pagination.dataset[key] = newPagination.dataset[key];
      } else {
        delete this.pagination.dataset[key];
      }
    });
  }

  syncGridMetadata(newGrid) {
    if (!this.grid || !newGrid) return;

    const resolvedTotal = this.grid.dataset.resolvedTotalProducts;
    const keys = [
      'loadingMode',
      'infiniteScroll',
      'totalProducts',
      'pageSize',
      'prefillTarget',
      'countMode',
      'hideUnavailableWithoutImage',
      'filterCountTemplate'
    ];

    keys.forEach((key) => {
      if (newGrid.dataset[key] != null) {
        this.grid.dataset[key] = newGrid.dataset[key];
      } else {
        delete this.grid.dataset[key];
      }
    });

    if (resolvedTotal) {
      this.grid.dataset.resolvedTotalProducts = resolvedTotal;
    }
  }

  async loadNextPage(options = {}) {
    if (this.isLoading) return false;
    if (!this.bufferedNodes.length && !this.getNextPageLink()) {
      this.updateLoadMoreVisibility();
      return false;
    }

    const {
      skipFillCheck = false,
      updateHistory = true,
      maxAppendProducts = this.getConfiguredPageSize()
    } = options;

    this.setLoadingState(true);

    // Trick para forçar o Safari (iOS) a renderizar a alteração de display:flex
    // ANTES de iniciar as tarefas pesadas de fetch e parsing.
    await new Promise(resolve => {
      requestAnimationFrame(() => requestAnimationFrame(resolve));
    });

    try {
      const productTarget = Number.isFinite(maxAppendProducts) && maxAppendProducts > 0
        ? maxAppendProducts
        : this.getConfiguredPageSize();
      let appendedProducts = 0;
      let didAppendAny = false;
      let lastFetchedUrl = null;

      while (appendedProducts < productTarget) {
        const remaining = productTarget - appendedProducts;
        const bufferedResult = this.flushBufferedNodes(remaining);

        appendedProducts += bufferedResult.appendedProducts;
        didAppendAny = didAppendAny || bufferedResult.appendedAny;

        if (appendedProducts >= productTarget) {
          break;
        }

        const nextLink = this.getNextPageLink();
        if (!nextLink) {
          break;
        }

        lastFetchedUrl = nextLink.href;

        const doc = await this.fetchPageDocument(lastFetchedUrl);
        const newGrid = doc.querySelector(this.selectors.grid);
        const newPagination = doc.querySelector(this.selectors.pagination);

        if (!newGrid) {
          break;
        }

        this.syncGridMetadata(newGrid);
        this.syncPagination(newPagination);

        const appendResult = this.appendNodesWithLimit(Array.from(newGrid.children), remaining);
        appendedProducts += appendResult.appendedProducts;
        didAppendAny = didAppendAny || appendResult.appendedAny;
        this.bufferedNodes.push(...appendResult.remainingNodes);

        if (!appendResult.appendedAny && !appendResult.remainingNodes.length) {
          break;
        }
      }

      if (updateHistory && lastFetchedUrl) {
        window.history.pushState({ path: lastFetchedUrl }, '', lastFetchedUrl);
        document.querySelector('link[rel="canonical"]')?.setAttribute('href', lastFetchedUrl);
      }

      return didAppendAny;
    } catch (error) {
      console.error('Error loading next page:', error);
      return false;
    } finally {
      this.setLoadingState(false);
      this.updateLoadMoreVisibility();

      if (!skipFillCheck && this.loadingMode === 'infinite' && !this.isAutoFilling) {
        this.scheduleFillCheck();
      }

      this.prefetchNextPage();
    }
  }

  async onFilterChange(customUrl = null) {
    if (!this.filterForm) return;

    const formData = new FormData(this.filterForm);
    const searchParams = new URLSearchParams(formData).toString();
    const url = customUrl || `${window.location.pathname}?${searchParams}`;

    const countEl = this.drawer?.querySelector(this.selectors.filterCount);
    if (countEl) {
      countEl.textContent = 'CARREGANDO...';
    }

    const openIndices = Array.from(this.drawer?.querySelectorAll('.filter-group.is-active') || [])
      .map((group) => group.dataset.index);

    try {
      const response = await fetch(url, {
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        }
      });
      const text = await response.text();
      const doc = new DOMParser().parseFromString(text, 'text/html');

      const newForm = doc.querySelector(this.selectors.filterForm);
      if (newForm && this.filterForm) {
        this.filterForm.innerHTML = newForm.innerHTML;

        openIndices.forEach((index) => {
          const group = this.filterForm.querySelector(`.filter-group[data-index="${index}"]`);
          group?.classList.add('is-active');
        });
      }

      const newTags = doc.querySelector(this.selectors.filterTags);
      const currentTags = this.drawer?.querySelector(this.selectors.filterTags);
      if (newTags && currentTags) {
        currentTags.innerHTML = newTags.innerHTML;
      }

      const newGrid = doc.querySelector(this.selectors.grid);
      if (newGrid && this.grid) {
        this.grid.replaceWith(newGrid);
      }

      this.refreshElements();
      this.initGrid();
      this.initGridMobile();

      const newPagination = doc.querySelector(this.selectors.pagination);
      this.syncPagination(newPagination);

      const newCountBtn = doc.querySelector(this.selectors.filterCount);
      if (newCountBtn && countEl) {
        countEl.innerHTML = newCountBtn.innerHTML;
      }

      this.clearPageCache();
      this.bufferedNodes = [];
      this.hasScrolled = false;
      this.resolvedTotalProducts = null;

      const countPromise = this.resolveTotalProducts();

      if (this.shouldUseDynamicLoading()) {
        await this.ensureInitialVisibleProducts();

        if (this.loadingMode === 'infinite') {
          this.scheduleFillCheck();
        }

        this.prefetchNextPage();
      } else {
        this.updateLoadMoreVisibility();
      }

      await countPromise;

      window.history.pushState({ path: url }, '', url);
      document.querySelector('link[rel="canonical"]')?.setAttribute('href', url);
    } catch (error) {
      console.error('Error updating filters:', error);
      if (countEl) {
        countEl.textContent = 'ERRO AO ATUALIZAR';
      }
    }
  }

  updateCount() {
    const total = this.getResolvedTotalProducts();
    const filterCount = this.drawer?.querySelector(this.selectors.filterCount);

    this.countDisplays.forEach((container) => {
      container.classList.toggle('is-loading', this.isCountLoading);

      const totalCountSpan = container.querySelector('[data-total-count]');
      if (totalCountSpan && !this.isCountLoading) {
        totalCountSpan.textContent = String(total);
      } else if (!totalCountSpan && !this.isCountLoading) {
        container.textContent = `${total} produtos`;
      }
    });

    if (filterCount) {
      if (this.isCountLoading) {
        filterCount.innerHTML = '<span class="collection-toolbar__spinner"></span>';
      } else {
        const template = this.grid?.dataset.filterCountTemplate || '__COUNT__';
        filterCount.textContent = template.replace('__COUNT__', String(total));
      }
    }
  }

  setPreFilling(state) {
    this.setCountLoading(state);
  }

  lockCount(state) {
    this.isCountLocked = state;
  }

  toggleDrawer(show) {
    if (!this.drawer || !this.overlay) return;

    if (show) {
      this.drawer.classList.add('is-open');
      this.overlay.classList.add('is-open');
      this.drawer.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
    } else {
      this.drawer.classList.remove('is-open');
      this.overlay.classList.remove('is-open');
      this.drawer.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    }
  }
}

document.addEventListener('DOMContentLoaded', () => {
  window.CollectionManager = new CollectionManager();
});
